if (!customElements.get('search-discovery')) {
  customElements.define('search-discovery', class extends HTMLElement {
    connectedCallback() {
      if (this.onSearch) return;
      const labels = JSON.parse(this.querySelector('[data-search-translations]').textContent);
      const format = (text, values) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
      const input = this.querySelector('[data-search-input]');
      const items = [...this.querySelectorAll('[data-faq-item]')];
      const normalize = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(document.documentElement.lang || 'en').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
      const records = items.map(item => ({ item, text: normalize(item.dataset.searchText) }));
      const initialQuery = input.value.trim();
      const hint = this.querySelector('[data-search-hint]');
      const count = this.querySelector('[data-faq-count]');
      const empty = this.querySelector('[data-faq-empty]');
      const panel = this.querySelector('[data-suggestions]');
      const products = this.querySelector('[data-suggestion-products]');
      const faqSuggestions = this.querySelector('[data-suggestion-faq]');
      const faqGroup = this.querySelector('[data-suggestion-faq-group]');
      const all = this.querySelector('[data-suggestion-all]');
      const status = this.querySelector('[data-suggestion-status]');
      let sequence = 0;
      const close = () => { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      const open = () => { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); };
      this.onPredict = () => {
        clearTimeout(this.timer);
        this.controller?.abort();
        const current = ++sequence;
        const query = input.value.trim();
        const terms = normalize(query).split(' ').filter(Boolean);
        if (query.length < 2 || !terms.length) { close(); return; }
        const matches = records.filter(({ text }) => terms.every(term => text.includes(term))).slice(0, 3);
        faqSuggestions.replaceChildren();
        matches.forEach(({ item }) => {
          const button = document.createElement('button');
          button.type = 'button';
          const title = item.querySelector('summary > span').cloneNode(true);
          title.querySelector('small')?.remove();
          button.textContent = title.textContent.trim();
          button.addEventListener('click', () => {
            close();
            item.hidden = false;
            item.open = true;
            item.scrollIntoView({ block: 'center' });
            item.querySelector('summary').focus({ preventScroll: true });
          });
          faqSuggestions.append(button);
        });
        faqGroup.hidden = matches.length === 0;
        const resultUrl = new URL(this.querySelector('form').action);
        resultUrl.searchParams.set('type', 'product');
        resultUrl.searchParams.set('q', query);
        all.href = resultUrl.href;
        products.textContent = labels.loading;
        status.textContent = format(labels.faq_loading, { faq: matches.length });
        open();
        this.timer = setTimeout(async () => {
          this.controller = new AbortController();
          const url = new URL(this.dataset.predictiveUrl, location.origin);
          url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '2', section_id: 'search-suggestions' });
          try {
            const response = await fetch(url, { signal: this.controller.signal });
            if (!response.ok) throw new Error('Suggestions unavailable');
            const html = await response.text();
            if (current !== sequence) return;
            const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-products]');
            if (!fragment) throw new Error('Suggestions missing');
            products.replaceChildren(...fragment.childNodes);
            status.textContent = format(labels.suggestions_count, { products: products.querySelectorAll('a').length, faq: matches.length });
          } catch (error) {
            if (current !== sequence || error.name === 'AbortError') return;
            products.textContent = labels.error_products;
            status.textContent = labels.error_status;
          }
        }, 200);
      };
      this.onKey = (event) => {
        if (event.key === 'Escape') { event.preventDefault(); input.focus(); ++sequence; clearTimeout(this.timer); this.controller?.abort(); close(); }
        if (event.key === 'ArrowDown' && !panel.hidden && event.target === input) {
          event.preventDefault();
          panel.querySelector('a, button')?.focus();
        }
      };
      this.onOutside = (event) => { if (!this.querySelector('.search-discovery__search-wrap').contains(event.target)) close(); };
      this.onFocus = () => { if (input.value.trim().length >= 2) this.onPredict(); };
      this.onSearch = () => {
        const terms = normalize(input.value).split(' ').filter(Boolean);
        let total = 0;
        hint.hidden = input.value.trim() === initialQuery;
        records.forEach(({ item, text }) => {
          const matches = terms.length ? terms.every(term => text.includes(term)) : item.dataset.faqDefault === 'true';
          item.hidden = !matches;
          if (!matches) item.open = false;
          if (matches) total++;
        });
        count.textContent = terms.length ? format(total === 1 ? labels.answer_one : labels.answer_other, { count: total }) : labels.default_answers;
        empty.hidden = total > 0;
        this.onPredict();
      };
      input.addEventListener('input', this.onSearch);
      this.addEventListener('keydown', this.onKey);
      document.addEventListener('pointerdown', this.onOutside);
      document.addEventListener('focusin', this.onOutside);
      input.addEventListener('focus', this.onFocus);
      // Populate the FAQ on load without opening the suggestion panel.
      this.onSearch();
      close();
      ++sequence;
      clearTimeout(this.timer);
    }
    disconnectedCallback() {
      this.querySelector('[data-search-input]')?.removeEventListener('input', this.onSearch);
      this.querySelector('[data-search-input]')?.removeEventListener('focus', this.onFocus);
      this.removeEventListener('keydown', this.onKey);
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('focusin', this.onOutside);
      clearTimeout(this.timer);
      this.controller?.abort();
      this.onSearch = null;
    }
  });
}
